import React, { ReactNode, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CellContext,
  ColumnHelper,
  TanstackTable,
  TanstackTableFilterVariant,
  defineColumns,
  useClientPagination,
} from '@thingslog/ui-components';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Widget } from './Widget';
import { DataResult, WidgetSettingsDto } from '@thingslog/repositories';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParsed, DataResultParseStream } from './parsing/DataResultParsed';
import {
  ChartEmptyState,
  ChartSeriesConfig,
  ChartWidgetFrame,
  CHART_DATA_POINT_COLOR_KEY,
  applyDataPointColors,
  createChartSeries,
  formatAxisValue,
  getChartDataPointColor,
} from './utils';
import { formatValueWithUnit } from './parsing/metadata';
import { useTranslation } from 'react-i18next';

interface TableComponentProps {
  paginated: boolean;
  parsedData: DataResultParsed;
  rows: TableDataRow[];
}

interface TableColumn {
  color: string;
  dataKey: string;
  label: string;
  unit?: string;
  colorByDataPoint?: boolean;
  valueType?: 'boolean';
}

type TableDataRow = { [key: string]: string | number | undefined };

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

const getXAxisDataKey = (parsedData: DataResultParsed): string => {
  return parsedData.xAxis?.dataKey ?? 'date';
};

const createTableColumns = (parsedData: DataResultParsed): TableColumn[] => {
  return createChartSeries(parsedData.dataStreams).map(
    (series: ChartSeriesConfig): TableColumn => ({
      color: series.color,
      dataKey: series.dataKey,
      label: series.name,
      unit: series.unit,
      colorByDataPoint: series.colorByDataPoint,
      valueType: series.valueType,
    })
  );
};

const formatTableValue = (value: string | number | undefined, unit?: string): string => {
  if (value === undefined || value === null) {
    return '';
  }

  const formattedValue = typeof value === 'number' ? value.toLocaleString() : String(value);
  return formatValueWithUnit(formattedValue, unit);
};

const isTruthyCellValue = (value: string | number | undefined): boolean => {
  return String(value).toLowerCase() === 'true' || value === 1;
};

const formatXAxisValue = (
  value: string | number | undefined,
  formatter?: (value: string) => string
): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return formatAxisValue(value, formatter);
};

const getFilterVariant = (column: TableColumn): TanstackTableFilterVariant => {
  return column.valueType === 'boolean' ? 'boolean' : 'range';
};

const TableComponent = ({
  paginated,
  parsedData,
  rows,
}: TableComponentProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const xAxisDataKey = getXAxisDataKey(parsedData);
  const tableColumns = useMemo(() => createTableColumns(parsedData), [parsedData]);
  const showRowColors = tableColumns.some(
    (column: TableColumn): boolean => !!column.colorByDataPoint
  );
  const xAxisLabel = parsedData.xAxis?.name || t('dashboard_timestamp', 'Timestamp');

  const columns = useMemo(
    () =>
      defineColumns<TableDataRow>((column: ColumnHelper<TableDataRow>) => [
        column.accessor(xAxisDataKey, {
          header: xAxisLabel,
          meta: { filterVariant: 'text', label: xAxisLabel, grow: true, minWidth: 180 },
          cell: (info: CellContext<TableDataRow, unknown>): ReactNode => {
            const rowColor = info.row.original[CHART_DATA_POINT_COLOR_KEY];

            return (
              <Box className="flex min-w-0 items-center gap-2">
                {showRowColors && (
                  <Box
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    sx={{
                      backgroundColor:
                        typeof rowColor === 'string'
                          ? rowColor
                          : getChartDataPointColor(info.row.index),
                    }}
                  />
                )}
                <span className="truncate">
                  {formatXAxisValue(
                    info.getValue() as string | number | undefined,
                    parsedData.xAxis?.formatter
                  )}
                </span>
              </Box>
            );
          },
        }),
        ...tableColumns.map((tableColumn: TableColumn) =>
          column.accessor(tableColumn.dataKey, {
            header: (): ReactNode => (
              <Box className="flex min-w-0 items-center justify-end gap-1.5">
                <Box
                  className="h-2 w-2 shrink-0 rounded-full"
                  sx={{ backgroundColor: tableColumn.color }}
                />
                <span className="truncate">{tableColumn.label}</span>
              </Box>
            ),
            meta: {
              filterVariant: getFilterVariant(tableColumn),
              label: tableColumn.label,
              align: 'right',
              headerAlign: 'right',
              minWidth: 160,
            },
            cell: (info: CellContext<TableDataRow, unknown>): ReactNode => {
              const value = info.getValue() as string | number | undefined;

              if (tableColumn.valueType === 'boolean') {
                return isTruthyCellValue(value) ? (
                  <CheckCircle fontSize="small" className="text-emerald-500" />
                ) : (
                  <Cancel fontSize="small" className="text-red-500" />
                );
              }

              if (value === undefined) {
                return (
                  <Typography component="span" className="text-sm text-slate-300">
                    -
                  </Typography>
                );
              }

              return formatTableValue(value, tableColumn.unit);
            },
          })
        ),
      ]),
    [parsedData, showRowColors, tableColumns, xAxisDataKey, xAxisLabel]
  );

  const clientPagination = useClientPagination({
    pageSizeOptions: ROWS_PER_PAGE_OPTIONS,
    minRows: 1,
    disabled: !paginated,
    resetOn: [rows],
  });

  if (rows.length === 0) {
    return null;
  }

  return (
    <ChartWidgetFrame className="p-0">
      <TanstackTable<TableDataRow>
        mode="client"
        className="min-h-0 flex-1 !border-0"
        classNames={{ container: 'widget-scrollbar' }}
        data={rows}
        columns={columns}
        getRowId={(row: TableDataRow): string => String(row[xAxisDataKey])}
        density="compact"
        stickyHeader
        enableSorting
        enableColumnFilters
        showToolbar={false}
        enablePagination={paginated}
        emptyMessage={t('dashboard_widget_no_rows', 'This widget has no rows to display.')}
        {...clientPagination.tableProps}
      />
    </ChartWidgetFrame>
  );
};

export class TableWidget implements Widget {
  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);

    if (normalizedSettings.type !== 'table') {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_table_settings_missing"
          description="Table settings are missing."
        />
      );
    }

    const dataParser = new DataResultParser(normalizedSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData =
      parsedData.dataStreams.length === 0 ||
      parsedData.dataStreams.every((stream: DataResultParseStream) => stream.data.length === 0);

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_rows"
          description="This widget has no rows to display."
        />
      );
    }

    const columns = createChartSeries(parsedData.dataStreams);
    const rows = applyDataPointColors(
      dataParser.merge(parsedData),
      columns.some((column: ChartSeriesConfig): boolean => !!column.colorByDataPoint)
    );

    return (
      <TableComponent
        paginated={normalizedSettings.visualization.paginated !== false}
        parsedData={parsedData}
        rows={rows}
      />
    );
  }
}
